import { plainToInstance } from 'class-transformer';
import { IsInt, IsOptional, IsString, Matches, Max, Min, validateSync } from 'class-validator';

// Deliberately not @nestjs/config — this service only needs to fail fast on a
// handful of required vars at boot, not a full injectable config module.
class EnvironmentVariables {
  @IsString()
  @Matches(/^postgres(ql)?:\/\//, { message: 'CHAT_DATABASE_URL must be a postgres connection string' })
  CHAT_DATABASE_URL!: string;

  // Guards the applications/API-key admin endpoints — a shared secret is
  // enough for a single-operator v1; replace with real admin auth once more
  // than one person needs to issue keys.
  @IsString()
  ADMIN_API_SECRET!: string;

  // Deliberately CHAT_PORT, not PORT — this workspace's root .env is shared
  // across projects (Nx auto-loads it for every task), and the main cart
  // API already claims PORT. A same-named var here would silently collide.
  @IsOptional()
  @IsInt()
  @Min(1)
  @Max(65535)
  CHAT_PORT?: number;

  // Shared with the cart API so AgentJwtGuard can verify admin login tokens
  // (agent endpoints). Optional: when unset the guard falls back to the same
  // dev default the API uses, so a plain local boot keeps working.
  @IsOptional()
  @IsString()
  AUTH_JWT_SECRET?: string;
}

export function validateEnv(config: Record<string, unknown>): EnvironmentVariables {
  const validated = plainToInstance(EnvironmentVariables, config, { enableImplicitConversion: true });
  const errors = validateSync(validated, { skipMissingProperties: false });

  if (errors.length > 0) {
    const details = errors
      .map((e) => Object.values(e.constraints ?? {}).join('; '))
      .join('\n  - ');
    throw new Error(`Invalid chat-service environment configuration:\n  - ${details}`);
  }

  return validated;
}
